import { useEffect } from "react";

const setMeta = (selector, value) => {
  let element = document.head.querySelector(selector);
  const created = !element;

  if (!value) {
    return { element, created: false, previous: element?.getAttribute("content") };
  }

  if (!element) {
    element = document.createElement("meta");
    const [, key, name] = selector.match(/meta\[(name|property)="([^"]+)"\]/) || [];
    if (key && name) element.setAttribute(key, name);
    document.head.appendChild(element);
  }

  const previous = element.getAttribute("content");
  element.setAttribute("content", value);
  return { element, created, previous };
};

const absoluteUrl = (value) => {
  if (!value || typeof window === "undefined") return value;
  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return value;
  }
};

const usePageMetadata = ({
  title,
  description,
  canonical,
  image,
  type = "website",
  structuredData,
}) => {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousTitle = document.title;
    if (title) document.title = title;

    const canonicalUrl = absoluteUrl(
      canonical || (typeof window !== "undefined" ? window.location.href : "")
    );
    const imageUrl = absoluteUrl(image);
    const records = [
      setMeta('meta[name="description"]', description),
      setMeta('meta[property="og:title"]', title),
      setMeta('meta[property="og:description"]', description),
      setMeta('meta[property="og:type"]', type),
      setMeta('meta[property="og:url"]', canonicalUrl),
      setMeta('meta[property="og:image"]', imageUrl),
      setMeta('meta[name="twitter:card"]', imageUrl ? "summary_large_image" : "summary"),
      setMeta('meta[name="twitter:title"]', title),
      setMeta('meta[name="twitter:description"]', description),
      setMeta('meta[name="twitter:image"]', imageUrl),
    ];

    let canonicalLink = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonicalLink;
    const previousCanonical = canonicalLink?.getAttribute("href");
    if (canonicalUrl) {
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute("href", canonicalUrl);
    }

    let schemaScript;
    if (structuredData) {
      schemaScript = document.createElement("script");
      schemaScript.type = "application/ld+json";
      schemaScript.dataset.thriveInsuranceBlogSchema = "true";
      schemaScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(schemaScript);
    }

    return () => {
      document.title = previousTitle;
      records.forEach(({ element, created, previous }) => {
        if (!element) return;
        if (created) element.remove();
        else if (previous === null || previous === undefined) element.removeAttribute("content");
        else element.setAttribute("content", previous);
      });
      if (canonicalLink) {
        if (canonicalCreated) canonicalLink.remove();
        else if (previousCanonical) canonicalLink.setAttribute("href", previousCanonical);
        else canonicalLink.removeAttribute("href");
      }
      schemaScript?.remove();
    };
  }, [canonical, description, image, structuredData, title, type]);
};

export default usePageMetadata;
